import type { BackendHttpDeps, BackendHttpRequest } from './backendHttpDeps'
import type { RemoteFunctionInvoker } from './callRemoteFunction'

type TagPlacement = 'query' | 'body' | undefined

function tagPlacement(request: BackendHttpRequest, gameId: string, url: URL): TagPlacement {
  const { method, body } = request
  const path = url.pathname
  const ugcPrefix = `/v1/ugc/${encodeURIComponent(gameId)}`
  if (path.startsWith(`${ugcPrefix}/`)) {
    const route = path.slice(ugcPrefix.length)
    if (method === 'GET' && (route === '/mine' || route === '/count')) return 'query'
    if (method === 'PUT' && /^\/entries\/[^/]+$/.test(route)) return 'body'
    if (
      method === 'POST' &&
      (route === '/entries' ||
        route === '/shared' ||
        route === '/browse' ||
        /^\/entries\/[^/]+\/(members|report)$/.test(route))
    )
      return 'body'
  }
  const shopPrefix = `/v1/shop/games/${encodeURIComponent(gameId)}`
  if (method === 'GET' && path.startsWith(`${shopPrefix}/`)) {
    const route = path.slice(shopPrefix.length)
    if (route === '/storefront' || /^\/items\/[^/]+$/.test(route)) return 'query'
  }
  if (method === 'POST' && path === '/v1/shop-orders' && body?.gameId === gameId) return 'body'
  if (
    method === 'GET' &&
    path === '/v1/direct-purchase/catalog' &&
    url.searchParams.get('gameId') === gameId
  )
    return 'query'
  return undefined
}

export function withReleaseTag(
  deps: BackendHttpDeps,
  gameId: string,
  versionTag: string | undefined
): BackendHttpDeps {
  return {
    ...deps,
    prepareRequest(input) {
      const request = deps.prepareRequest?.(input) ?? input
      const url = new URL(request.path, 'http://sdk.invalid')
      switch (tagPlacement(request, gameId, url)) {
        case 'query': {
          if (!versionTag && !url.searchParams.has('tagId')) return request
          url.searchParams.delete('tagId')
          if (versionTag) url.searchParams.set('tagId', versionTag)
          return { ...request, path: `${url.pathname}${url.search}${url.hash}` }
        }
        case 'body': {
          if (!versionTag && !Object.hasOwn(request.body ?? {}, 'tagId')) return request
          const body = { ...request.body }
          delete body.tagId
          if (versionTag) body.tagId = versionTag
          return { ...request, body }
        }
        default:
          return request
      }
    },
  }
}

const leaderboardFunctions = new Set([
  'apiCreateScoreToken',
  'apiSubmitScore',
  'apiGetPagedScores',
  'apiGetMyRank',
  'apiGetPodiumScores',
])

export function withRemoteReleaseTag(
  invoke: RemoteFunctionInvoker,
  versionTag: string | undefined
): RemoteFunctionInvoker {
  return async <TResponse>(functionName: string, payload?: unknown): Promise<TResponse> => {
    const isLeaderboard = leaderboardFunctions.has(functionName)
    if (
      !isLeaderboard &&
      functionName !== 'h5/liveops/config' &&
      !functionName.startsWith('h5/simulation/')
    ) {
      return invoke<TResponse>(functionName, payload)
    }
    if (
      payload !== undefined &&
      (payload === null || typeof payload !== 'object' || Array.isArray(payload))
    ) {
      throw new TypeError('Release-scoped RPC payload must be an object')
    }
    // Leaderboard preserves an explicit empty tag. Other APIs omit it.
    const effectiveTag = isLeaderboard ? versionTag : versionTag || undefined
    if (payload === undefined && effectiveTag === undefined)
      return invoke<TResponse>(functionName, payload)
    const body: Record<string, unknown> = { ...payload }
    delete body.versionTag
    if (effectiveTag !== undefined) body.versionTag = effectiveTag
    return invoke<TResponse>(functionName, body)
  }
}
